import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { Plus, UserPlus, Lock, Globe, Trophy, Copy, Search, Star, Share2 } from "lucide-react";
import { shareOrCopyInvite, getInviteUrl, getInviteDisplayUrl } from "@/lib/shareUtils";
import { analytics } from "@/lib/analytics";
import { EmojiPicker } from "@/components/leagues/EmojiPicker";
import LeagueCard from "@/components/leagues/LeagueCard";
import PublicLeagueCard from "@/components/leagues/PublicLeagueCard";

const Leagues = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [showCreatedCode, setShowCreatedCode] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);

  // Create league form
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newIsPublic, setNewIsPublic] = useState(false);
  const [newEmoji, setNewEmoji] = useState("🏆");
  const [creating, setCreating] = useState(false);

  // Join league form
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  // Fetch user's leagues
  const { data: myLeagues = [], isLoading: loadingMine } = useQuery({
    queryKey: ["my-leagues", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_members")
        .select("id, league_id, role, is_favourite, leagues(id, name, member_count, is_public, invite_code, description, emoji)")
        .eq("user_id", user!.id);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch public leagues
  const { data: publicLeagues = [], isLoading: loadingPublic } = useQuery({
    queryKey: ["public-leagues", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("id, name, member_count, is_public, description, emoji")
        .eq("is_public", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Fetch standings for each league using the same RPC as the detail page
  const myLeagueIds = myLeagues.map((m) => m.league_id);
  const { data: leagueStandings = {} } = useQuery({
    queryKey: ["league-standings-positions", user?.id, myLeagueIds],
    enabled: !!user && myLeagueIds.length > 0,
    queryFn: async () => {
      const results: Record<string, { rank: number | null; hasResults: boolean }> = {};
      await Promise.all(
        myLeagueIds.map(async (leagueId) => {
          const { data } = await supabase.rpc("get_league_standings", {
            p_league_id: leagueId,
          });
          const standings = data ?? [];
          const hasResults = standings.some((s: any) => s.total_points > 0);
          const rawRank = standings.findIndex((s: any) => s.user_id === user!.id) + 1;
          results[leagueId] = {
            rank: hasResults && rawRank > 0 ? rawRank : null,
            hasResults,
          };
        })
      );
      return results;
    },
  });

  const getLeagueInfo = (leagueId: string): { rank: number | null; hasResults: boolean } => {
    return leagueStandings[leagueId] ?? { rank: null, hasResults: false };
  };

  const myLeagueIdSet = new Set(myLeagueIds);
  const browsableLeagues = publicLeagues.filter((l) => !myLeagueIdSet.has(l.id));

  const favouriteCount = myLeagues.filter((m) => m.is_favourite).length;

  // Filter helpers
  const filteredMyLeagues = myLeagues.filter((m) => {
    const league = m.leagues as any;
    if (!league) return false;
    if (showFavouritesOnly && !m.is_favourite) return false;
    if (searchQuery && !league.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const filteredPublicLeagues = browsableLeagues.filter((l) => {
    if (searchQuery && !l.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleCreate = async () => {
    if (!newName.trim() || !user) return;
    setCreating(true);
    try {
      const { data: code, error: codeErr } = await supabase.rpc("generate_invite_code");
      if (codeErr) throw codeErr;

      const { data: league, error: leagueErr } = await supabase
        .from("leagues")
        .insert({
          name: newName.trim(),
          description: newDesc.trim() || null,
          is_public: newIsPublic,
          invite_code: code,
          created_by: user.id,
          emoji: newEmoji,
        })
        .select("id, invite_code")
        .single();
      if (leagueErr) throw leagueErr;

      const { error: memberErr } = await supabase
        .from("league_members")
        .insert({ league_id: league.id, user_id: user.id, role: "owner" });
      if (memberErr) throw memberErr;

      queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
      queryClient.invalidateQueries({ queryKey: ["public-leagues"] });
      analytics.track("league_created", { is_public: newIsPublic });
      setShowCreatedCode(league.invite_code);
      setNewName("");
      setNewDesc("");
      setNewIsPublic(false);
      setNewEmoji("🏆");
    } catch (e: any) {
      toast({ title: "Failed to create league", description: e.message, variant: "destructive" });
    }
    setCreating(false);
  };

  const handleJoin = async () => {
    if (!joinCode.trim() || !user) return;
    setJoining(true);
    try {
      const { data: league, error: lookupErr } = await supabase
        .from("leagues")
        .select("id, name")
        .eq("invite_code", joinCode.trim().toUpperCase())
        .single();
      if (lookupErr || !league) {
        toast({ title: "League not found", description: "Check your invite code and try again.", variant: "destructive" });
        setJoining(false);
        return;
      }

      if (myLeagueIdSet.has(league.id)) {
        toast({ title: "Already a member", description: `You're already in ${league.name}.` });
        setJoining(false);
        return;
      }

      const { error: joinErr } = await supabase
        .from("league_members")
        .insert({ league_id: league.id, user_id: user.id, role: "member" });
      if (joinErr) throw joinErr;

      queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
      queryClient.invalidateQueries({ queryKey: ["public-leagues"] });
      analytics.track("league_joined", { method: "invite_code" });
      setJoinOpen(false);
      setJoinCode("");
      toast({ title: `Joined ${league.name}!` });
      navigate(`/leagues/${league.id}`);
    } catch (e: any) {
      toast({ title: "Failed to join", description: e.message, variant: "destructive" });
    }
    setJoining(false);
  };

  const handleJoinPublic = async (leagueId: string, leagueName: string) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from("league_members")
        .insert({ league_id: leagueId, user_id: user.id, role: "member" });
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["my-leagues"] });
      queryClient.invalidateQueries({ queryKey: ["public-leagues"] });
      analytics.track("league_joined", { method: "public_browse" });
      toast({ title: `Joined ${leagueName}!` });
    } catch (e: any) {
      toast({ title: "Failed to join", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-2xl space-y-5">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold md:text-3xl">Leagues</h1>
          <div className="flex gap-2">
            <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setShowCreatedCode(null); setNewEmoji("🏆"); } }}>
              <DialogTrigger asChild>
                <Button className="font-semibold">
                  <Plus className="mr-1 h-4 w-4" /> Create
                </Button>
              </DialogTrigger>
              <DialogContent>
                {showCreatedCode ? (
                  <>
                    <DialogHeader>
                      <DialogTitle>League Created! 🎉</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      {/* Option 1: Invite Code */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Share code</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 rounded-lg bg-muted px-4 py-3 text-center text-2xl font-mono font-bold tracking-widest">
                            {showCreatedCode}
                          </code>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(showCreatedCode);
                              toast({ title: "Code copied!" });
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* OR divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
                      </div>

                      {/* Option 2: Share Link */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">Share link</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 rounded-lg bg-muted px-3 py-3 text-center text-sm font-mono font-semibold truncate">
                            {getInviteDisplayUrl(showCreatedCode)}
                          </code>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => shareOrCopyInvite(showCreatedCode, toast)}
                          >
                            {navigator.share ? <Share2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>

                      <Button className="w-full font-semibold" onClick={() => { setCreateOpen(false); setShowCreatedCode(null); }}>
                        Done
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <DialogHeader>
                      <DialogTitle>Create a League</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        <Label>League Name</Label>
                        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. The Pit Crew" maxLength={50} />
                      </div>
                      <div className="space-y-2">
                        <Label>Emoji</Label>
                        <EmojiPicker value={newEmoji} onChange={setNewEmoji} />
                      </div>
                      <div className="space-y-2">
                        <Label>Description (optional)</Label>
                        <Textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="What's your league about?" rows={3} />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-3">
                        <div className="flex items-center gap-2">
                          {newIsPublic ? <Globe className="h-4 w-4 text-muted-foreground" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                          <span className="text-sm font-medium">{newIsPublic ? "Public" : "Private"}</span>
                        </div>
                        <Switch checked={!newIsPublic} onCheckedChange={(checked) => setNewIsPublic(!checked)} />
                      </div>
                      <Button className="w-full font-semibold" disabled={!newName.trim() || creating} onClick={handleCreate}>
                        {creating ? "Creating…" : "Create League"}
                      </Button>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>

            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="font-semibold">
                  <UserPlus className="mr-1 h-4 w-4" /> Join
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Join a League</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Invite Code</Label>
                    <Input
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      placeholder="e.g. ABC123"
                      maxLength={6}
                      className="text-center text-lg font-mono tracking-widest"
                    />
                  </div>
                  <Button className="w-full font-semibold" disabled={joinCode.length < 6 || joining} onClick={handleJoin}>
                    {joining ? "Joining…" : "Join League"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="my" className="space-y-4">
          <TabsList className="w-full">
            <TabsTrigger value="my" className="flex-1">My Leagues ({myLeagues.length})</TabsTrigger>
            <TabsTrigger value="public" className="flex-1">Public Leagues ({browsableLeagues.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="my" className="space-y-3">
            {/* Favourites filter */}
            {myLeagues.length > 0 && (
              <button
                onClick={() => setShowFavouritesOnly((v) => !v)}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  showFavouritesOnly
                    ? "border-yellow-400 bg-yellow-400/10 text-yellow-600"
                    : "border-border text-muted-foreground hover:border-yellow-400/50"
                }`}
              >
                <Star className={`h-3 w-3 ${showFavouritesOnly ? "fill-yellow-400 text-yellow-400" : ""}`} />
                Favourites only ({favouriteCount})
              </button>
            )}

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search leagues…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {loadingMine ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : filteredMyLeagues.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
                  <Trophy className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {myLeagues.length === 0 ? "You haven't joined any leagues yet." : "No leagues match your filters."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {filteredMyLeagues.map((m) => {
                  const league = m.leagues as any;
                  if (!league) return null;
                  return (
                    <LeagueCard
                      key={league.id}
                      league={league}
                      isFavourite={!!m.is_favourite}
                      isOwner={m.role === "owner"}
                      userId={user!.id}
                      membershipId={m.id}
                      rank={getLeagueInfo(league.id).rank}
                      totalMembers={league.member_count ?? 0}
                      hasResults={getLeagueInfo(league.id).hasResults}
                    />
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="public" className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search public leagues…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {loadingPublic ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : filteredPublicLeagues.length === 0 ? (
              <p className="text-sm text-muted-foreground">No public leagues available to join.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {filteredPublicLeagues.map((league) => (
                  <PublicLeagueCard key={league.id} league={league} onJoin={handleJoinPublic} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Leagues;
